import * as React from 'react';
import { Reservation, ReservationController } from '../resController';
import { translate } from '@data/translations';
import { AccommodationPricelistModel, ReservationModel } from '@common/modelDefinition';
import useStore from '@data/state/zustand';
import { isPriceEditLocked } from '../utils';

interface OpenPriceProps {
    reservation: Reservation;
    isPending?: boolean;
}

const OpenPrice = (props: OpenPriceProps) => {
    const { reservation } = props;
    const [inputValue, setInputValue] = React.useState<string>('');
    const [suggestedPrice, setSuggestedPrice] = React.useState<number | null>(null);
    const [isInitialized, setIsInitialized] = React.useState(false);
    const reservations: Reservation[] = useStore((appState: any) => appState.state.reservationForm2.reservations);

    const memoReservation = React.useMemo(() => reservation, [reservation]);

    const priceListsRaw = useStore((state) => state.monkeys.filteredAccommodationPriceLists());
    const accommodationPricelists = React.useMemo(() => priceListsRaw, [priceListsRaw]);

    React.useEffect(() => {
        if (memoReservation && !isInitialized) {
            const { openPrice } = memoReservation;

            if (openPrice !== undefined && openPrice !== null) {
                setInputValue(String(openPrice));
                setIsInitialized(true);
            } else {
                const roomInfo = memoReservation.RoomInfo;
                if (roomInfo.accommodationPriceListId) {
                    const { adultsNumber } = memoReservation;
                    const targetPriceList = accommodationPricelists?.find(
                        (a: AccommodationPricelistModel) => a.id === roomInfo.accommodationPriceListId
                    );
                    let adultsNumberPriceIndex = adultsNumber === 0 ? 0 : adultsNumber - 1;
                    const accomodationPriceRules =
                        targetPriceList && targetPriceList.accomodationPriceRules
                            ? typeof targetPriceList.accomodationPriceRules === 'string'
                                ? JSON.parse(targetPriceList.accomodationPriceRules)
                                : targetPriceList.accomodationPriceRules
                            : [];
                    if (adultsNumber >= accomodationPriceRules.length) {
                        adultsNumberPriceIndex = accomodationPriceRules.length - 1;
                    }
                    const _openPrice = accomodationPriceRules[adultsNumberPriceIndex];

                    if (suggestedPrice === null && _openPrice !== undefined) {
                        setSuggestedPrice(_openPrice);
                    }
                }
                setIsInitialized(true);
            }
        }
    }, [memoReservation, accommodationPricelists, isInitialized, suggestedPrice]);

    const ctrl = React.useMemo(() => new ReservationController(), []);

    const updateGroupResOpenPrice = React.useCallback(
        (price: number | null) => {
            if (reservations && reservations?.length > 1) {
                for (let i = 0; i < reservations.length; i++) {
                    if (
                        reservations[i] &&
                        !reservations[i].id &&
                        !reservations[i].accommodationPriceListId &&
                        !reservations[i].openPrice
                    ) {
                        ctrl.setActiveReservationIndex(i);
                        const res = { ...reservations[i] };
                        res.openPrice = price;
                        ctrl.updateActiveReservation(res as ReservationModel);
                    }
                }
            }
        },
        [ctrl, reservations]
    );

    const handleApply = React.useCallback(() => {
        if (isPriceEditLocked(reservation)) {
            return;
        }
        const res = { ...reservation };
        const parsed = inputValue === '' ? null : Number(inputValue);
        res.openPrice = parsed;
        ctrl.updateActiveReservation(res, undefined, true);
        updateGroupResOpenPrice(parsed);
    }, [reservation, inputValue, ctrl, updateGroupResOpenPrice]);

    if (!reservation) {
        return null;
    }

    return (
        <div className="">
            <label className="text-bold ml-4p mb-4p mt-2p">{translate('Custom price (opt.)')}</label>
            <input
                disabled={reservation.canceledReservationInvoicingStatus === 2 || isPriceEditLocked(reservation)}
                className={
                    reservation.canceledReservationInvoicingStatus !== 1 ? 'input input-modal ' : 'input input-modal '
                }
                placeholder={translate(
                    reservation.canceledReservationInvoicingStatus === 1
                        ? 'Cancellation fee'
                        : suggestedPrice !== null
                        ? String(suggestedPrice)
                        : 'Room price'
                )}
                autoComplete="off"
                type="number"
                value={inputValue}
                onChange={(e) => {
                    const value = e.target.value;

                    // Prazno = OK (brisanje)
                    if (value === '') {
                        setInputValue('');
                        return;
                    }

                    // Dopusti samo brojeve do 2 decimale
                    const regex = /^\d*\.?\d{0,2}$/;
                    if (regex.test(value)) {
                        setInputValue(value);
                    }
                }}
                onKeyPress={(event) => {
                    if (event.key === 'Enter') {
                        handleApply();
                    }
                }}
            />
            {inputValue !== '' ? (
                <div onClick={handleApply} className="color-primary">{translate('Confirm with Enter')}</div>
            ) : null}
            {window.innerWidth < 768 && inputValue !== '' ? (
                <div onClick={handleApply} className="button-primary mt-8p">
                    {translate('Apply')}
                </div>
            ) : null}
        </div>
    );
};

export default OpenPrice;